import React, { useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import { mediaQueryUp } from 'components/utils/breakpoint'
import BedbankOfferSummary from './BedbankOfferSummary'
import ShareWithFriendsButton from 'components/Common/ShareWithFriendsButton'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import ShareModal from 'components/Common/Modals/ShareModal'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import OfferUrgencyCountdownLabel from 'components/Common/Labels/OfferUrgencyCountdownLabel'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import LayoutContainer from 'components/Common/LayoutContainer/LayoutContainer'
import { HeaderHeightContext } from 'contexts/HeaderStateContext'
import { useAppSelector } from 'hooks/reduxHooks'
import BedbankPropertyDetails from './BedbankPropertyDetails'
import ExportMediaContentButton from 'components/Common/ExportMediaContentButton/ExportMediaContentButton'
import config from 'constants/config'
import { buildAgentHubQuotingURL } from 'lib/agentHub/quoting'
import type { ShareConfig } from 'components/Common/Modals/ShareModal/ShareModal'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import useBedbankFlightPrice from 'hooks/OfferPage/useBedbankFlightPrice'
import { EmptyArray } from 'lib/array/arrayUtils'
import { getCanAgentExportMedia } from 'agentHub/selectors/agentHubSelectors'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { getCanUseMultiRoomUI } from 'selectors/offerPage/bedbankOfferPageSelectors'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import ContentTagLabels from 'components/Content/ContentTagLabels'
import OfferPageTrustPilot from '../OfferPageTrustPilot'
import getTasteOfGoldPromoCampaign from 'takeovers/tasteOfGoldTakeover/selectors/getTasteOfGoldPromoCampaign'
import isTasteOfGoldEligibleOffer from 'takeovers/tasteOfGoldTakeover/utils/isTasteOfGoldEligibleOffer'
import { TasteOfGoldPromoCard } from 'takeovers/tasteOfGoldTakeover/components/TasteOfGoldPromoStyles'
import TasteOfGoldPromoContent from 'takeovers/tasteOfGoldTakeover/components/TasteOfGoldPromoContent'

const RootContainer = styled(LayoutContainer)`
  padding-top: ${rem(20)};
  padding-bottom: ${rem(56)};
`

const SummaryArea = styled.div`
  display: none;

  ${mediaQueryUp.tablet} {
    position: relative;
    align-self: stretch;
    display: block;
  }
`

const StickySummaryContent = styled.div`
  position: sticky;
  top: 0;
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
`

interface Props {
  offer: App.BedbankOffer
  duration: number
  checkInDate?: string
  checkOutDate?: string
  onViewPackageOptions?: () => void
  onCheckAvailabilityClick?: () => void
  rooms?: Array<OfferPageRoom>
  loadingPrices?: boolean
  flightPrice?: number
  flightsEnabled: boolean
  shareModalConfig: ShareConfig
  airport?: App.Airport
  canUseMultiRoomUI: boolean
}

function BedbankOfferIntro(props: Props) {
  const {
    checkInDate,
    checkOutDate,
    offer,
    onViewPackageOptions,
    onCheckAvailabilityClick,
    rooms = EmptyArray,
    loadingPrices,
    flightsEnabled,
    duration,
    shareModalConfig,
    airport,
    canUseMultiRoomUI,
  }: Props = props

  const flightPrice = useBedbankFlightPrice(offer, {
    rooms,
    startDate: checkInDate,
    endDate: checkOutDate,
    originAirportCode: airport?.code,
  })

  const { heights } = useContext(HeaderHeightContext)
  const location = offer.property.locationTitleOverride ?
    offer.property.locationTitleOverride :
      [offer.property.address.city, offer.property.address.stateProvinceName]
        .filter(Boolean)
        .join(', ')

  const hasPromotions = offer.promotions.length > 0

  const bedBankOfferPromotionEndDate = offer.promotions[0]?.periodSchedule.to

  const region = useAppSelector(state => state.geo.currentRegionCode)
  const currency = useAppSelector(state => state.geo.currentCurrency)
  const isExportMediaEnabled = config.EXPORT_MEDIA_CONTENT_ENABLED
  const isAgentHubEnabled = config.agentHub.isEnabled
  const mediaExportURL = isAgentHubEnabled ? buildAgentHubQuotingURL(offer.id, { currency, region }) : undefined
  const canAgentExportMedia = getCanAgentExportMedia

  const shouldShowToolTip = useScreenSizeUp('tablet')

  const isTasteOfGoldActive = !!useAppSelector(getTasteOfGoldPromoCampaign)
  const highestBedbankPrice = useMemo(() => {
    const prices = offer.packages.map(p => p.sell?.price).filter((p): p is number => p != null)
    return prices.length > 0 ? Math.max(...prices) : 0
  }, [offer.packages])
  const showTasteOfGoldPromo = isTasteOfGoldActive && isTasteOfGoldEligibleOffer(
    offer,
    currency,
    offer.property.address.countryCode?.toLowerCase() !== 'au',
    highestBedbankPrice,
  )

  return (
    <RootContainer>
      <Group direction="vertical" gap={16}>
        <Group
          direction="vertical"
          tabletDirection="horizontal-reverse"
          horizontalAlign="space-between"
          verticalAlign="center"
          gap={4}
          noDisplayWhenEmpty
        >
          <CSSBreakpoint min="tablet">
            <Group
              direction="horizontal"
              gap={8}
              verticalAlign="center"
              horizontalAlign="end"
            >
              {isExportMediaEnabled && canAgentExportMedia && <TripGuard>
                <ExportMediaContentButton href={mediaExportURL} target="_blank" />
              </TripGuard>}
              <TripGuard>
                <ShareModal shareConfig={shareModalConfig} openOnHash="#share">
                  <ShareWithFriendsButton showToolTip={shouldShowToolTip} />
                </ShareModal>
                <BookmarkButton offer={offer} />
              </TripGuard>

              <TripGuard inverse>
                <ShareModal shareConfig={shareModalConfig} openOnHash="#share">
                  <ShareWithFriendsButton horizontalOutdent={isExportMediaEnabled ? undefined : 'end'} showToolTip={shouldShowToolTip} />
                </ShareModal>
                <BookmarkButton offer={offer} />
              </TripGuard>
              {isExportMediaEnabled && canAgentExportMedia && <TripGuard inverse>
                <ExportMediaContentButton href={mediaExportURL} target="_blank" />
              </TripGuard>}
            </Group>
          </CSSBreakpoint>

          <LabelGroup>
            {hasPromotions && <>
              <ProductTypeLabel productType="limited_time_special" />
              {bedBankOfferPromotionEndDate && (
                <OfferUrgencyCountdownLabel
                  endDate={bedBankOfferPromotionEndDate}
                  tagPlace="bedbank-offer-intro"
                />
              )}
            </>}
            <ContentTagLabels offerId={offer.id} page="offer" />
          </LabelGroup>
        </Group>
        <Group direction="horizontal" gap={48}>
          <Group direction="vertical" gap={16}>
            <BedbankPropertyDetails
              offer={offer}
              location={location}
              hasPromotions={hasPromotions}
              checkInDate={checkInDate}
              checkOutDate={checkOutDate}
              duration={duration}
              rooms={rooms}
              flightPrice={flightPrice}
              flightsEnabled={flightsEnabled}
            />
            {showTasteOfGoldPromo && <TasteOfGoldPromoCard>
              <CSSBreakpoint max="mobile">
                <TasteOfGoldPromoContent layout="vertical" size="medium" context="offerPage" />
              </CSSBreakpoint>
              <CSSBreakpoint min="tablet">
                <TasteOfGoldPromoContent layout="horizontal" size="medium" context="offerPage" />
              </CSSBreakpoint>
            </TasteOfGoldPromoCard>}
          </Group>
          <SummaryArea>
            <StickySummaryContent
              style={{
                top: rem(heights.bedbankOfferHeader + 16),
              }}
            >
              <BedbankOfferSummary
                loadingPrices={loadingPrices}
                onViewPackageOptions={onViewPackageOptions}
                onCheckAvailabilityClick={onCheckAvailabilityClick}
                checkInDate={checkInDate}
                checkOutDate={checkOutDate}
                duration={duration}
                offer={offer}
                rooms={rooms}
                flightPrice={flightPrice}
                flightsEnabled={flightsEnabled}
                canUseMultiRoomUI={canUseMultiRoomUI}
              />
              <OfferPageTrustPilot />
            </StickySummaryContent>
          </SummaryArea>
        </Group>
      </Group>
    </RootContainer>
  )
}

type MappedProps = Pick<Props,
  | 'airport'
  | 'duration'
  | 'checkInDate'
  | 'checkOutDate'
  | 'offer'
  | 'rooms'
  | 'loadingPrices'
  | 'canUseMultiRoomUI'
> & { open: boolean }

export default withOfferPageAndReduxState((offerState, appState): MappedProps => ({
  airport: offerState.airport,
  duration: offerState.duration,
  checkInDate: offerState.checkInDate,
  checkOutDate: offerState.checkOutDate,
  offer: offerState.bedbankOffer!,
  rooms: offerState.rooms,
  open: !!offerState.activeFilter,
  loadingPrices: !!appState.offer.offerRatesLoading[offerState.bedbankOffer!.id],
  canUseMultiRoomUI: getCanUseMultiRoomUI(offerState, appState),
}), BedbankOfferIntro, preventRenderWithOpenFilter)
